import type { DataSourceRequestState, SortDescriptor } from "@progress/kendo-data-query";
import { GridColumn, GridNoRecords, type GridDataStateChangeEvent, type GridPageChangeEvent, type GridSortChangeEvent } from "@progress/kendo-react-grid";
import { Card, CardBody, CardTitle } from "@progress/kendo-react-layout";
import { useState } from "react";
import { useGetSevenDayUsageRecordSummaryQuery, useGetThirtyDayUsageRecordSummaryQuery, useListUsageRecordsQuery } from "../../api/admin/adminUsageRecordApi";
import AppGrid from "../../components/grid/appGrid";
import type { UsageRecordSummaryDto } from "../../contracts";
import { toDate } from "../../utils/dateValueConversion";
import { formatNumber } from "../../utils/numberValueConversion";

const initialSort: SortDescriptor[] = [{ field: "recordedUtc", dir: "desc" }];

interface UsageSummaryCardProps {
    title: string;
    summary?: UsageRecordSummaryDto;
    isFetching: boolean;
}

const UsageSummaryCard: React.FC<UsageSummaryCardProps> = ({ title, summary, isFetching }) => (
    <Card className="h-100">
        <CardBody>
            <CardTitle>{title}</CardTitle>
            <div className="row g-3 mt-1">
                <div className="col-6 col-xl">
                    <div className="small text-muted">Cached input</div>
                    <div className="fs-5 fw-semibold">{isFetching ? "…" : formatNumber(summary?.cached_input_token_count)}</div>
                </div>
                <div className="col-6 col-xl">
                    <div className="small text-muted">Uncached input</div>
                    <div className="fs-5 fw-semibold">{isFetching ? "…" : formatNumber(summary?.uncached_input_token_count)}</div>
                </div>
                <div className="col-6 col-xl">
                    <div className="small text-muted">Output</div>
                    <div className="fs-5 fw-semibold">{isFetching ? "…" : formatNumber(summary?.output_token_count)}</div>
                </div>
                <div className="col-6 col-xl">
                    <div className="small text-muted">Total tokens</div>
                    <div className="fs-5 fw-semibold">{isFetching ? "…" : formatNumber(summary?.total_token_count)}</div>
                </div>
                <div className="col-6 col-xl">
                    <div className="small text-muted">Records</div>
                    <div className="fs-5 fw-semibold">{isFetching ? "…" : formatNumber(summary?.record_count)}</div>
                </div>
            </div>
        </CardBody>
    </Card>
);

const UsageRecordManagement: React.FC = () => {
    const [dataState, setDataState] = useState<DataSourceRequestState>({ skip: 0, take: 50, sort: initialSort });
    const { data, isFetching, isError } = useListUsageRecordsQuery(dataState);
    const { data: sevenDayData, isFetching: isSevenDayFetching, isError: isSevenDayError } = useGetSevenDayUsageRecordSummaryQuery();
    const { data: thirtyDayData, isFetching: isThirtyDayFetching, isError: isThirtyDayError } = useGetThirtyDayUsageRecordSummaryQuery();

    const records = (data?.data ?? []).map(record => ({
        ...record,
        recordedUtc: toDate(record.recorded_utc),
        customerName: record.customer_name,
        cachedInputTokenCount: record.cached_input_token_count,
        uncachedInputTokenCount: record.uncached_input_token_count,
        outputTokenCount: record.output_token_count,
        totalTokenCount: record.cached_input_token_count + record.uncached_input_token_count + record.output_token_count
    }));

    const pageChange = (event: GridPageChangeEvent): void => setDataState(previous => ({
        ...previous,
        skip: event.page.skip,
        take: event.page.take
    }));
    const dataStateChange = (event: GridDataStateChangeEvent): void => setDataState({
        ...event.dataState,
        skip: event.dataState.skip,
        take: event.dataState.take
    });
    const sortChange = (event: GridSortChangeEvent): void => setDataState(previous => ({ ...previous, sort: event.sort, skip: 0 }));

    return (
        <div className="d-flex flex-column gap-3 p-3 h-100 min-h-0 overflow-hidden">
            <div className="row g-3">
                <div className="col-12 col-xl-6">
                    <UsageSummaryCard title="Last 7 days" summary={sevenDayData?.usage_record_summary} isFetching={isSevenDayFetching} />
                </div>
                <div className="col-12 col-xl-6">
                    <UsageSummaryCard title="Last 30 days" summary={thirtyDayData?.usage_record_summary} isFetching={isThirtyDayFetching} />
                </div>
            </div>
            {(isError || isSevenDayError || isThirtyDayError) && <div className="alert alert-danger mb-0" role="alert">Unable to load usage reporting. Please try again.</div>}
            <div className="flex-grow-1 min-h-0 overflow-hidden">
                <AppGrid
                    showLoader={isFetching}
                    dataItemKey="id"
                    scrollable="scrollable"
                    sortable={true}
                    filterable={false}
                    data={records}
                    resizable={true}
                    initialState={dataState}
                    onPageChange={pageChange}
                    onDataStateChange={dataStateChange}
                    sort={dataState.sort}
                    total={data?.total ?? 0}
                    skip={dataState.skip}
                    take={dataState.take}
                    defaultSort={initialSort}
                    onSortChange={sortChange}
                    pageable={{ buttonCount: 3, pageSizes: [50, 100, 200] }}
                    style={{ height: "100%" }}
                >
                    <GridNoRecords>{!isFetching && <span className="text-muted">{isError ? "Unable to load usage records. Please try again." : "No usage records found."}</span>}</GridNoRecords>
                    <GridColumn field="recordedUtc" title="Recorded UTC" filter="date" format="{0:dd MMM yyyy HH:mm:ss}" width="210px" />
                    <GridColumn field="customerName" title="Customer" width="200px" />
                    <GridColumn field="provider" title="Provider" width="160px" />
                    <GridColumn field="agent" title="Agent" width="180px" />
                    <GridColumn field="model" title="Model" width="180px" />
                    <GridColumn field="cachedInputTokenCount" title="Cached Input Tokens" filter="numeric" format="{0:n0}" width="190px" />
                    <GridColumn field="uncachedInputTokenCount" title="Uncached Input Tokens" filter="numeric" format="{0:n0}" width="210px" />
                    <GridColumn field="outputTokenCount" title="Output Tokens" filter="numeric" format="{0:n0}" width="150px" />
                    <GridColumn field="totalTokenCount" title="Total Tokens" filterable={false} sortable={false} format="{0:n0}" width="150px" />
                </AppGrid>
            </div>
        </div>
    );
};

export default UsageRecordManagement;
